import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserPlusIcon,
  DocumentArrowUpIcon,
  ServerStackIcon,
  BriefcaseIcon,
  IdentificationIcon,
  AcademicCapIcon,
  EnvelopeIcon,
  ClipboardDocumentCheckIcon,
  UserCircleIcon,
  CheckBadgeIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid, MagnifyingGlassIcon } from '@heroicons/react/24/solid';

// --- Configuration and Styling ---
const PRIMARY_COLOR_CLASS = 'teal'; // Theme color: teal

// --- Utility Functions for Simulation ---

const generatePassword = () => {
    // Generates a complex, temporary password
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let password = '';
    for (let i = 0; i < 12; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
};

// --- Mock Data Extraction (Simulating OCR/NLP) ---
const mockExtractedData = (input) => {
    // Phase 1 inputs (Name, Email) are used to define the mock profile.
    const { fullName, personalEmail } = input;
    
    // All other fields are simulated as being extracted from the Offer Letter (OCR)
    const baseCTC = 1950000; // Total CTC
    const annualBase = Math.round(baseCTC * 0.55); 
    const hra = Math.round(annualBase * 0.45);
    const conveyance = 1200 * 12;
    const specialAllowance = baseCTC - annualBase - hra - conveyance;


    return {
        // Extracted from Offer/Input (Phase 2)
        name: fullName || "Vivek Chandra",
        email: personalEmail || "vivek.chandra@candidate.com",
        mobile: "98765 43210", // OCR extracted or defaulted
        dateOfJoining: "2025-12-01", // OCR extracted
        jobTitle: "Principal Data Scientist", // OCR extracted
        department: "Advanced Analytics", // OCR extracted
        reportingManager: "Deepa Narayan (deepa.n@company.com)", // OCR extracted
        jobLocation: "Hyderabad, IN", // OCR extracted

        // Extracted from CTC Breakup (Phase 2) - FULL PAY BREAKDOWN
        annualCTC: baseCTC,
        compensation: {
            baseSalary: annualBase,
            hra: hra,
            conveyance: conveyance,
            specialAllowance: specialAllowance,
            totalMonthly: Math.round(baseCTC / 12),
        },
        // Extracted Education/Experience summary (Phase 2)
        educationSummary: "M.Tech, IIT Madras (2015)",
        experienceSummary: "10 Years in Data Science",

        // Placeholder for data filled by candidate (Phase 4 - Self-Onboarding)
        candidateFilled: {
            isComplete: false,
            address: "A-502, Phoenix Towers, Hitech City, Hyderabad, 500081",
            panNumber: "ABCDE1234F",
            bankAccountNumber: "001234567890",
            emergencyContact: "Sunita Chandra, Mother, 9876511122",
            isBankDetailsVerified: false,
            isAddressVerified: false,
        }
    };
};

// Mock function to simulate system credential generation (Phase 3)
const generateSystemCredentials = (name) => {
    const nameParts = name.toLowerCase().split(' ');
    const userHandle = nameParts.length > 1 ? `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1]}` : nameParts[0];

    return {
        officialEmail: `${userHandle}@acmeco.com`,
        temporaryPassword: generatePassword(),
        employeeId: `EMP-${Math.floor(Math.random() * 9000 + 1000)}`,
        teamsLogin: userHandle,
        internalPortalAccess: "Standard Employee Role",
        idCardNumber: `C-${Math.floor(Math.random() * 10000000).toString().padStart(7, '0')}`, // New: Auto-generated ID Card Number
    };
};

// --- Step Mapping for Sidebar (6 Phases) ---
const steps = [
  { id: 1, name: 'Phase 1: Admin Profile Creation', icon: UserPlusIcon, title: "Initial Data Entry by HR/Admin" },
  { id: 2, name: 'Phase 2: Document Upload & OCR', icon: DocumentArrowUpIcon, title: "System Extracts Core Job/Comp Data" },
  { id: 3, name: 'Phase 3: Credential Generation', icon: ServerStackIcon, title: "Official Login & Employee ID Assigned" },
  { id: 4, name: 'Phase 4: Candidate Self-Onboarding', icon: UserCircleIcon, title: "Candidate Submits Statutory/Bank Info" },
  { id: 5, name: 'Phase 5: HR Verification & Audit', icon: MagnifyingGlassIcon, title: "HR Audits Submitted Information" },
  { id: 6, name: 'Phase 6: Official Onboarding', icon: ClipboardDocumentCheckIcon, title: "Final System Sync & Notifications" },
];

// --- Sub-components ---

const StepCard = ({ children, title }) => (
  <motion.div
    key={title}
    initial={{ x: 50, opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
    exit={{ x: -50, opacity: 0 }}
    transition={{ duration: 0.3 }}
    className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 w-full"
    style={{ fontFamily: 'Roboto, sans-serif' }}
  >
    <h2 className={`text-3xl font-normal text-${PRIMARY_COLOR_CLASS}-800 mb-6 border-b pb-4 border-gray-100`} style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h2>
    {children}
  </motion.div>
);

const InputGroup = ({ label, name, value, onChange, type, placeholder, required = true }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
        <input
            id={name}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className="mt-1 w-full px-4 py-2 rounded-lg bg-gray-50 border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500 transition duration-150"
        />
    </div>
);

const FileInput = ({ name, label, file, onChange }) => (
    <label htmlFor={name} className={`flex flex-col items-center justify-center p-3 h-24 border-2 border-dashed border-${PRIMARY_COLOR_CLASS}-300 rounded-xl cursor-pointer bg-${PRIMARY_COLOR_CLASS}-50/50 hover:bg-${PRIMARY_COLOR_CLASS}-100 transition duration-150 shadow-md`}>
        <input id={name} name={name} type="file" onChange={onChange} className="sr-only" accept=".pdf,.doc,.docx" />
        <DocumentArrowUpIcon className={`h-5 w-5 text-${PRIMARY_COLOR_CLASS}-600 mb-1`} />
        <p className={`text-xs font-medium text-${PRIMARY_COLOR_CLASS}-800 truncate w-full text-center`}>
            {file ? file.name : label}
        </p>
    </label>
);

const DisplayItem = ({ label, value, isSystemExtracted = false, isCurrency = false, className = '' }) => (
    <div className={`flex flex-col p-3 rounded-lg bg-gray-50 shadow-sm ${className}`}>
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <div className="flex justify-between items-center mt-1">
            <span className={`text-base font-normal ${isSystemExtracted ? `text-${PRIMARY_COLOR_CLASS}-700` : 'text-gray-800'}`}>
                {isCurrency ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value) : (value || 'N/A')}
            </span>
            {isSystemExtracted && (
                <span className={`text-xs text-${PRIMARY_COLOR_CLASS}-500 bg-${PRIMARY_COLOR_CLASS}-100 px-2 py-0.5 rounded-full`}>Auto-read</span>
            )}
        </div>
    </div>
);

const CredentialItem = ({ label, value, icon: Icon, isSensitive = false }) => (
    <div className="p-4 bg-gray-100 rounded-xl shadow-inner border border-gray-200">
        <div className="flex items-center space-x-2 mb-1">
            <Icon className={`h-5 w-5 text-${PRIMARY_COLOR_CLASS}-600`} />
            <p className="text-sm font-medium text-gray-700">{label}</p>
        </div>
        <p className={`text-xl font-normal ${isSensitive ? 'text-red-600' : 'text-gray-800'} break-all`}>
            {value}
        </p>
    </div>
);

// --- Main Component ---

const OnboardingProcessV4 = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        // Phase 1 Admin Input (Only Name, Email required, Phone optional)
        fullName: '', personalEmail: '', phone: '',
        // Phase 2 Documents (Only Offer Letter & Resume)
        offerLetterFile: null, resumeFile: null,
    });
    const [candidateProfile, setCandidateProfile] = useState({});
    const [systemCredentials, setSystemCredentials] = useState({});
    const [isFinished, setIsFinished] = useState(false);
    const [reviewTab, setReviewTab] = useState('job'); // Tabs for Phase 5

    // Combined profile for final review
    const finalProfile = useMemo(() => ({
        ...candidateProfile,
        ...systemCredentials,
    }), [candidateProfile, systemCredentials]);


    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleFileChange = useCallback((e) => {
        const { name, files } = e.target;
        setFormData(prev => ({ ...prev, [name]: files[0] }));
    }, []);

    const isPhase1Valid = () => formData.fullName.trim() && formData.personalEmail.trim();

    // --- Phase 2 Logic: Document Upload & Extraction ---
    const handleExtractData = async () => {
        if (!isPhase1Valid() || !formData.offerLetterFile) {
            console.error("Validation Error: Please complete Phase 1 fields and upload the Offer Letter.");
            return;
        }

        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate OCR/NLP delay

        const extractedData = mockExtractedData(formData);
        setCandidateProfile(extractedData);

        setIsLoading(false);
        setCurrentStep(3); // Skip to Phase 3 (Credential Generation) after data is ready
    };

    // --- Phase 3 Logic: Credential Generation ---
    const handleGenerateCredentials = async () => {
        if (!candidateProfile.name) {
            console.error("Missing Profile: Cannot generate credentials without extracted name.");
            return;
        }
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        const generatedCreds = generateSystemCredentials(candidateProfile.name);
        setSystemCredentials(generatedCreds);

        setIsLoading(false);
        setCurrentStep(4);
    };

    // --- Phase 4 Logic: Simulate Candidate Self-Onboarding ---
    const handleSimulateSelfOnboarding = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1500));

        setCandidateProfile(prev => ({
            ...prev,
            candidateFilled: {
                ...prev.candidateFilled,
                isComplete: true
            }
        }));
        setIsLoading(false);
        setCurrentStep(5);
    };

    // --- Phase 5 Logic: HR Audit ---
    const handleStartHRVerification = () => {
        // In a real app, this would update internal system status flags
        setCurrentStep(6);
    }

    // --- Phase 6 Logic: Finalize ---
    const handleFinalize = async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate final HRMS sync

        console.log("Final Employee Onboarding Payload:", finalProfile);
        setIsLoading(false);
        setIsFinished(true);
    };

    const renderReviewTabContent = () => {
        const profile = finalProfile;

        if (!profile.name) return <p className="text-center text-gray-500 p-10">Data not yet extracted. Please complete Phase 2.</p>;

        const tabs = {
            job: {
                title: "Job, Compensation & Basic Profile (System Auto-read)",
                content: (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
                        <DisplayItem label="Job Title" value={profile.jobTitle} isSystemExtracted={true} />
                        <DisplayItem label="Department" value={profile.department} isSystemExtracted={true} />
                        <DisplayItem label="Date of Joining" value={profile.dateOfJoining} isSystemExtracted={true} />
                        <DisplayItem label="Reporting Manager" value={profile.reportingManager} isSystemExtracted={true} />
                        <DisplayItem label="Job Location" value={profile.jobLocation} isSystemExtracted={true} />
                        <DisplayItem label="Candidate Phone" value={profile.mobile} isSystemExtracted={true} />
                         <DisplayItem label="Annual CTC (Offer Letter)" value={profile.compensation?.annualCTC} isSystemExtracted={true} isCurrency={true} className="col-span-1 md:col-span-2 text-2xl" />
                        
                        <h4 className="text-lg font-normal text-gray-700 col-span-2 mt-4 border-t pt-4">Compensation Breakdown</h4>
                        <DisplayItem label="Base Salary" value={profile.compensation?.baseSalary} isSystemExtracted={true} isCurrency={true} />
                        <DisplayItem label="HRA" value={profile.compensation?.hra} isSystemExtracted={true} isCurrency={true} />
                        <DisplayItem label="Conveyance" value={profile.compensation?.conveyance} isSystemExtracted={true} isCurrency={true} />
                        <DisplayItem label="Special Allowance" value={profile.compensation?.specialAllowance} isSystemExtracted={true} isCurrency={true} />
                    </div>
                )
            },
            candidate: {
                title: "Candidate Self-Submitted Data (Requires Audit)",
                content: (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
                        <DisplayItem label="Residential Address" value={profile.candidateFilled?.address} isSystemExtracted={false} />
                        <DisplayItem label="PAN / SSN" value={profile.candidateFilled?.panNumber} isSystemExtracted={false} />
                        <DisplayItem label="Bank A/C Number" value={profile.candidateFilled?.bankAccountNumber} isSystemExtracted={false} />
                        <DisplayItem label="Emergency Contact" value={profile.candidateFilled?.emergencyContact} isSystemExtracted={false} />
                        <div className="md:col-span-2">
                            <label className="text-sm font-medium text-gray-700 block mb-2">Verification Status</label>
                            <div className="flex space-x-4">
                                <button
                                    onClick={() => setCandidateProfile(p => ({ ...p, candidateFilled: { ...p.candidateFilled, isBankDetailsVerified: !p.candidateFilled.isBankDetailsVerified } }))}
                                    className={`px-4 py-2 text-sm rounded-full transition-colors font-medium ${profile.candidateFilled.isBankDetailsVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                >
                                    <CheckSolid className="h-4 w-4 inline mr-1" />
                                    Bank Details: {profile.candidateFilled.isBankDetailsVerified ? 'Verified' : 'Unverified'}
                                </button>
                                <button
                                    onClick={() => setCandidateProfile(p => ({ ...p, candidateFilled: { ...p.candidateFilled, isAddressVerified: !p.candidateFilled.isAddressVerified } }))}
                                    className={`px-4 py-2 text-sm rounded-full transition-colors font-medium ${profile.candidateFilled.isAddressVerified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                                >
                                    <CheckSolid className="h-4 w-4 inline mr-1" />
                                    Address Proof: {profile.candidateFilled.isAddressVerified ? 'Verified' : 'Unverified'}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        };

        return (
            <>
                <h3 className={`text-xl font-normal text-${PRIMARY_COLOR_CLASS}-800 mb-3 border-b pb-2`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {tabs[reviewTab].title}
                </h3>
                {tabs[reviewTab].content}
            </>
        );
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <StepCard title={steps[0].title}>
                        <p className="text-gray-600 mb-6 text-sm">
                            Enter the minimal identifying information required to initiate the candidate profile. All other details will be read from the Offer Letter.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <InputGroup label="Full Name" name="fullName" value={formData.fullName} onChange={handleInputChange} type="text" placeholder="Jane Doe" />
                            <InputGroup label="Personal Email" name="personalEmail" value={formData.personalEmail} onChange={handleInputChange} type="email" placeholder="jane.doe@personal.com" />
                            <InputGroup label="Phone (Optional)" name="phone" value={formData.phone} onChange={handleInputChange} type="tel" placeholder="9876543210" required={false} />
                        </div>
                    </StepCard>
                );

            case 2:
                return (
                    <StepCard title={steps[1].title}>
                        <p className="text-gray-600 mb-6 text-sm">
                            Upload the Offer Letter (containing job details and pay breakdown) and the candidate's Resume. The system will auto-read all missing profile fields.
                        </p>
                        {/* Phase 2: Removed extra placeholder and adjusted grid to 2 columns */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> 
                            <FileInput name="offerLetterFile" label="1. Offer Letter (MANDATORY)" file={formData.offerLetterFile} onChange={handleFileChange} />
                            <FileInput name="resumeFile" label="2. Resume / CV" file={formData.resumeFile} onChange={handleFileChange} />
                        </div>
                        {candidateProfile.name && (
                             <motion.div initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className={`text-${PRIMARY_COLOR_CLASS}-600 bg-${PRIMARY_COLOR_CLASS}-50 p-4 rounded-lg mt-6 text-sm flex items-center shadow-md font-normal`}>
                                <CheckSolid className="h-5 w-5 mr-2" />
                                **Extraction Complete:** Core profile and detailed compensation data is ready for credential generation.
                            </motion.div>
                        )}
                    </StepCard>
                );

            case 3:
                return (
                    <StepCard title={steps[2].title}>
                        <p className="text-gray-600 mb-6 text-sm">
                            The system automatically generates all necessary digital credentials. These credentials should be securely communicated to the candidate.
                        </p>
                        {/* Phase 3: Updated to explicitly show all credentials, including temporary password */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <CredentialItem label="Employee ID" value={systemCredentials.employeeId} icon={IdentificationIcon} />
                            <CredentialItem label="ID Card Number (Auto-Generated)" value={systemCredentials.idCardNumber} icon={CheckBadgeIcon} />
                            <CredentialItem label="Official Email (Outlook)" value={systemCredentials.officialEmail} icon={EnvelopeIcon} />
                            <CredentialItem label="Teams Login" value={systemCredentials.teamsLogin} icon={ServerStackIcon} />
                            
                            {/* NEW: Showing Temporary Password to Admin */}
                            <CredentialItem label="Temporary Password" value={systemCredentials.temporaryPassword} icon={KeyIcon} />

                            <div className="p-4 bg-yellow-50 rounded-xl shadow-inner border border-yellow-200 flex items-center justify-center">
                                <p className="text-sm font-normal text-yellow-800">
                                    This password is temporary and **must** be changed upon first login.
                                </p>
                            </div>
                        </div>
                    </StepCard>
                );

            case 4:
                return (
                    <StepCard title={steps[3].title}>
                        <div className={`p-6 border-2 border-dashed border-${PRIMARY_COLOR_CLASS}-300 rounded-2xl bg-${PRIMARY_COLOR_CLASS}-50/50 mb-6 shadow-lg text-center`}>
                            <UserCircleIcon className={`h-12 w-12 text-${PRIMARY_COLOR_CLASS}-600 mx-auto mb-3`} />
                            <p className="text-xl font-normal text-gray-800 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>Candidate Onboarding Portal Awaiting Submission</p>
                            <p className="text-sm text-gray-600">
                                The candidate is currently completing mandatory statutory forms (Bank details, Address, ID Proof, PAN/SSN, etc.).
                            </p>
                        </div>
                        {finalProfile.candidateFilled?.isComplete ? (
                            <div className="text-center p-4 bg-green-100 text-green-700 rounded-lg font-medium flex items-center justify-center">
                                <CheckSolid className="h-5 w-5 mr-2" />
                                Candidate Forms Submitted! Ready for HR Verification.
                            </div>
                        ) : (
                            <button
                                onClick={handleSimulateSelfOnboarding}
                                disabled={isLoading}
                                className={`w-full px-6 py-2 rounded-full bg-green-600 text-white font-medium shadow-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center text-base`}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        Simulating Submission...
                                    </>
                                ) : (
                                    'Simulate Candidate Submission'
                                )}
                            </button>
                        )}
                    </StepCard>
                );

            case 5:
                return (
                    <StepCard title={steps[4].title}>
                        <p className="text-gray-600 mb-6 text-sm">
                            HR is required to verify the integrity and accuracy of the documents extracted by the system and the information submitted by the candidate.
                        </p>

                        <div className="border-b border-gray-200 mb-6">
                            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                                {['job', 'candidate'].map((tabId) => (
                                    <button
                                        key={tabId}
                                        onClick={() => setReviewTab(tabId)}
                                        className={`flex items-center space-x-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                                            reviewTab === tabId
                                                ? `border-${PRIMARY_COLOR_CLASS}-600 text-${PRIMARY_COLOR_CLASS}-600`
                                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                        }`}
                                    >
                                        {tabId === 'job' ? <BriefcaseIcon className="h-5 w-5" /> : <AcademicCapIcon className="h-5 w-5" />}
                                        <span>{tabId === 'job' ? 'Job & Comp Audit' : 'Candidate Data Audit'}</span>
                                    </button>
                                ))}
                            </nav>
                        </div>
                        {renderReviewTabContent()}
                    </StepCard>
                );

            case 6:
                return (
                    <StepCard title={steps[5].title}>
                         <div className={`p-8 border-2 border-dashed border-${PRIMARY_COLOR_CLASS}-300 rounded-2xl bg-${PRIMARY_COLOR_CLASS}-50/50 mb-6 shadow-lg`}>
                            <h3 className="text-xl font-normal text-gray-800 mb-3 flex items-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                <ClipboardDocumentCheckIcon className={`h-6 w-6 mr-2 text-${PRIMARY_COLOR_CLASS}-600`} />
                                Final System Provisioning Checklist
                            </h3>
                            <ul className="space-y-3 text-sm text-gray-700 font-normal">
                                <li className="flex justify-between items-center">
                                    <span>Employee Profile Created (HRMS)</span>
                                    <CheckSolid className="h-5 w-5 text-green-500" />
                                </li>
                                <li className="flex justify-between items-center">
                                    <span>Reporting Structure Mapped</span>
                                    <CheckSolid className="h-5 w-5 text-green-500" />
                                </li>
                                <li className="flex justify-between items-center">
                                    <span>Access Permissions Granted (Level 1)</span>
                                    <CheckSolid className="h-5 w-5 text-green-500" />
                                </li>
                                <li className="flex justify-between items-center text-red-700 font-medium">
                                    <span>Asset/Device Request Triggered (LPT-ACME-0815)</span>
                                    <CheckSolid className="h-5 w-5 text-yellow-500" />
                                </li>
                                <li className="flex justify-between items-center text-red-700 font-medium">
                                    <span>ID Card Printing Triggered (ID: {finalProfile.idCardNumber})</span>
                                    <CheckSolid className="h-5 w-5 text-yellow-500" />
                                </li>
                            </ul>
                        </div>
                        <p className="text-sm text-gray-600 text-center font-normal">
                            The profile is ready to be locked and officially moved to "Active" status.
                        </p>
                    </StepCard>
                );

            default:
                return null;
        }
    };

    const handleNext = () => {
        if (currentStep === 1) {
            if (isPhase1Valid()) {
                setCurrentStep(2);
            }
        } else if (currentStep === 2) {
            handleExtractData();
        } else if (currentStep === 3) {
            handleGenerateCredentials();
        } else if (currentStep === 4) {
            if (finalProfile.candidateFilled?.isComplete) {
                setCurrentStep(5);
            }
        } else if (currentStep === 5) {
             setCurrentStep(6);
        }
    };


    return (
        <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Roboto, sans-serif' }}>
             {/* Note: In a production environment, link tags for Poppins and Roboto would be in the index.html head */}
            
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                <header className="mb-10 p-6 bg-white border-b border-gray-100 shadow-lg rounded-2xl">
                    <h1 className="text-4xl font-normal text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        HRMS | Enterprise Onboarding Automation
                    </h1>
                    <p className={`text-xl font-normal text-${PRIMARY_COLOR_CLASS}-600 mt-1`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {steps[currentStep - 1]?.name}: {steps[currentStep - 1]?.title}
                    </p>
                </header>

                <div className="flex flex-col lg:flex-row gap-10">
                    {/* Step Indicator (Sidebar) */}
                    <div className="lg:w-1/4">
                        <div className={`sticky top-4 p-6 bg-white rounded-2xl shadow-xl border border-${PRIMARY_COLOR_CLASS}-100`}>
                            <h3 className="text-xl font-normal text-gray-800 mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>6-Phase Onboarding Pipeline</h3>
                            <ol className="space-y-6">
                                {steps.map((step) => (
                                    <li key={step.id} className="relative flex items-start group">
                                        <div className="flex items-center h-full">
                                            <span className={`h-full w-0.5 ${step.id < currentStep ? 'bg-green-400' : 'bg-gray-200'} absolute top-8 left-4 -ml-px`} aria-hidden="true" />
                                            <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                                                step.id === currentStep
                                                ? `border-${PRIMARY_COLOR_CLASS}-600 bg-${PRIMARY_COLOR_CLASS}-50 shadow-md`
                                                : step.id < currentStep
                                                ? 'bg-green-500 border-green-500 shadow-md'
                                                : 'border-gray-300 bg-white'
                                            }`}>
                                                {step.id < currentStep ? (
                                                    <CheckSolid className="h-5 w-5 text-white" />
                                                ) : (
                                                    <step.icon className={`h-5 w-5 ${step.id === currentStep ? `text-${PRIMARY_COLOR_CLASS}-600` : 'text-gray-500'}`} />
                                                )}
                                            </span>
                                        </div>
                                        <p className={`ml-4 text-sm flex flex-col ${step.id <= currentStep ? 'font-medium text-gray-900' : 'text-gray-500'}`}>
                                            {step.name}
                                            <span className="text-xs text-gray-400 font-normal">{step.title}</span>
                                        </p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="lg:w-3/4">
                        {isFinished ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white p-16 rounded-3xl shadow-2xl text-center border border-green-200"
                            >
                                <CheckSolid className="h-28 w-28 text-green-500 mx-auto mb-8 animate-pulse" />
                                <h2 className="text-4xl font-normal text-green-700 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Phase 6 Complete: Employee Officially Onboarded!</h2>
                                <p className="text-xl text-gray-600 font-normal">
                                    **{finalProfile.name} (ID: {finalProfile.employeeId})** has been moved to an ACTIVE state in HRMS.
                                </p>
                                <p className={`text-lg text-${PRIMARY_COLOR_CLASS}-600 mt-2 font-medium`}>
                                    System triggered Asset/ID Card requests and final welcome notification.
                                </p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className={`mt-8 px-6 py-2 rounded-full bg-${PRIMARY_COLOR_CLASS}-600 text-white font-medium shadow-lg hover:bg-${PRIMARY_COLOR_CLASS}-700 transition-colors text-base`}
                                >
                                    Start New Onboarding
                                </button>
                            </motion.div>
                        ) : (
                            <AnimatePresence mode="wait">
                                {renderCurrentStep()}
                            </AnimatePresence>
                        )}

                        {/* Navigation Buttons - Removed white background and shadow */}
                        {!isFinished && (
                            <div className={`flex justify-between items-center mt-8 p-0 pt-5`}>
                                {currentStep > 1 && (
                                    <button
                                        onClick={() => setCurrentStep(currentStep - 1)}
                                        disabled={isLoading}
                                        className="px-6 py-2 rounded-full text-gray-700 font-medium bg-gray-100 border border-gray-300 hover:bg-gray-200 transition-colors disabled:opacity-50 shadow-md text-base"
                                    >
                                        &larr; Back
                                    </button>
                                )}
                                <div className={currentStep === 1 ? "ml-auto" : ""}>
                                    {currentStep < 6 ? (
                                        <button
                                            onClick={handleNext}
                                            disabled={isLoading || (currentStep === 1 && !isPhase1Valid()) || (currentStep === 2 && !formData.offerLetterFile) || (currentStep === 4 && !finalProfile.candidateFilled?.isComplete)}
                                            className={`px-6 py-2 rounded-full bg-${PRIMARY_COLOR_CLASS}-600 text-white font-medium shadow-xl shadow-${PRIMARY_COLOR_CLASS}-300 hover:bg-${PRIMARY_COLOR_CLASS}-700 transition-colors disabled:opacity-50 text-base flex items-center`}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                    {currentStep === 2 ? 'Extracting Data...' : currentStep === 3 ? 'Generating Credentials...' : 'Processing...'}
                                                </>
                                            ) : (
                                                <>
                                                    {currentStep === 2 ? 'Start Document Extraction' : currentStep === 4 ? 'Continue to HR Audit' : currentStep === 5 ? 'Final System Sync' : 'Next Step'} &rarr;
                                                </>
                                            )}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleFinalize}
                                            disabled={isLoading}
                                            className="px-6 py-2 rounded-full bg-green-600 text-white font-medium shadow-xl shadow-green-300 hover:bg-green-700 transition-colors disabled:opacity-50 text-base flex items-center"
                                        >
                                            {isLoading ? (
                                                 <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                    Finalizing System Sync...
                                                </>
                                            ) : (
                                                'Finalize Onboarding'
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingProcessV4;